/**
 * Keeps block-style toolbar commands from losing or hiding the user's selection:
 * - restore a range captured before the menu blurs the editor
 * - remap it through the transform
 * - convert AllSelection to TextSelection so the highlight shows while focused
 * - widen single-block ranges when the whole textblock was restyled
 */
import type { Editor } from '@tiptap/core';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import { AllSelection, NodeSelection, type Selection, TextSelection } from '@tiptap/pm/state';
import { Mapping } from '@tiptap/pm/transform';

export type StoredSelectionRange = { from: number; to: number };

function collapseSelectionToTextblockEnd(editor: Editor) {
  editor.commands.selectTextblockEnd();

  const anchor = editor.state.selection.$anchor;
  if (anchor.parent.isTextblock) {
    const end = anchor.end();
    if (editor.state.selection.from !== end || editor.state.selection.to !== end) {
      editor.commands.setTextSelection(end);
    }
    return;
  }

  const lastChild = editor.state.doc.lastChild;
  if (lastChild?.isTextblock) {
    editor.commands.setTextSelection(editor.state.doc.content.size - 1);
  }
}

function resolveBeforeSelection(
  editor: Editor,
  storedSelection: StoredSelectionRange | null,
): Selection {
  const current = editor.state.selection;
  if (!storedSelection || storedSelection.from === storedSelection.to) {
    return current;
  }

  const { doc } = editor.state;
  if (storedSelection.from === 0 && storedSelection.to === doc.content.size) {
    return new AllSelection(doc);
  }

  return TextSelection.create(doc, storedSelection.from, storedSelection.to);
}

/** AllSelection is invisible while the editor is focused — map to a real text range. */
function toVisibleTextSelection(doc: ProseMirrorNode, selection: Selection): TextSelection | null {
  if (selection instanceof NodeSelection) {
    return null;
  }

  if (selection instanceof TextSelection) {
    return selection;
  }

  if (!(selection instanceof AllSelection)) {
    return null;
  }

  const first = doc.firstChild;
  const last = doc.lastChild;
  if (!first?.isTextblock || !last?.isTextblock) {
    return null;
  }

  const from = doc.resolve(1).start();
  const lastBlockPos = doc.content.size - last.nodeSize;
  const to = doc.resolve(lastBlockPos + 1).end();

  return TextSelection.create(doc, from, to);
}

function finalizeBlockStyleSelection(
  doc: ProseMirrorNode,
  selection: Selection,
): TextSelection | null {
  const visible = toVisibleTextSelection(doc, selection);
  if (!visible || visible.empty) {
    return visible;
  }

  const { $from, $to } = visible;
  if ($from.parent === $to.parent && $from.parent.isTextblock) {
    return TextSelection.create(doc, $from.start(), $from.end());
  }

  return visible;
}

function restoreEditorSelection(editor: Editor, selection: TextSelection) {
  editor.commands.setTextSelection({ from: selection.from, to: selection.to });
  editor.commands.focus();
}

/**
 * TipTap's clearNodes walks state.doc with tr.selection. Chaining it after unsetBlockquote
 * remaps a mid-node selection (e.g. starting on hardBreak) so defaultType becomes hardBreak
 * and setNodeMarkup throws RangeError. Dispatch each step as its own command instead.
 */
export function resetBlockWrappers(editor: Editor) {
  editor.commands.focus();
  if (editor.isActive('blockquote')) {
    editor.commands.unsetBlockquote();
  }
  editor.commands.clearNodes();
}

/** Remap a pre-command range through block-style transforms; collapse only when mapping fails. */
export function applyBlockStyleChange(
  editor: Editor,
  apply: () => void,
  storedSelection: StoredSelectionRange | null = null,
) {
  const beforeSelection = resolveBeforeSelection(editor, storedSelection);
  if (beforeSelection.empty) {
    apply();
    editor.commands.focus();
    return;
  }

  // Selector open can collapse selection; restore so apply() hits the captured range.
  editor.view.dispatch(editor.state.tr.setSelection(beforeSelection));

  const combinedMapping = new Mapping();
  const onTransaction = ({
    transaction,
  }: {
    transaction: { docChanged: boolean; mapping: Mapping };
  }) => {
    if (transaction.docChanged) {
      combinedMapping.appendMapping(transaction.mapping);
    }
  };

  editor.on('transaction', onTransaction);
  try {
    apply();
  } finally {
    editor.off('transaction', onTransaction);
  }

  const mapped = beforeSelection.map(editor.state.doc, combinedMapping);
  const finalized = mapped ? finalizeBlockStyleSelection(editor.state.doc, mapped) : null;

  if (finalized) {
    restoreEditorSelection(editor, finalized);
    return;
  }

  collapseSelectionToTextblockEnd(editor);
  editor.commands.focus();
}
